import { first } from 'lodash/fp';
import sortBy from 'lodash/fp/sortBy';
import type { CurrentUserDto } from '../../../../types/UserDtos';
import { useAppContext } from '../../hooks/useAppContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import { isAnyCycleAdmin, isDecisionMaker } from '../../services/User';
import { releaseNotes, type ReleaseNote } from './releaseNotes';

const JUST_LAUNCHED_LOCAL_STORAGE_KEY = 'JustLaunchedLink';

const getLatestLaunchedUpdate = (lastUpdated: number | null, now: number) =>
    lastUpdated !== null && lastUpdated <= now ? lastUpdated : 0;

export const hasUpdateJustLaunched = (lastUpdated: number | null, now: number) =>
    lastUpdated !== null && lastUpdated <= now;

export const shouldShowJustLaunched = (
    lastUpdated: number | null,
    now: number,
    lastDismissedAt: number,
    currentUser: CurrentUserDto
) => {
    const latestUpdate = getLatestLaunchedUpdate(lastUpdated, now);
    const isDismissed = latestUpdate > 0 && lastDismissedAt >= latestUpdate;
    const relevantUser = isRelevantUser(currentUser);
    const launchedBeforeCompanyRegistered = currentUser.company.dateCreated > latestUpdate;

    if (isDismissed || !relevantUser || launchedBeforeCompanyRegistered) {
        return false;
    }

    return hasUpdateJustLaunched(lastUpdated, now);
};

const isRelevantUser = (currentUser: CurrentUserDto) => {
    const cycleAdmin = isAnyCycleAdmin(currentUser);
    const decisionMaker = isDecisionMaker(currentUser, false);
    const importantManager = currentUser.reports.length >= 5;

    return decisionMaker || cycleAdmin || importantManager;
};

export const useJustLaunched = () => {
    const { user } = useAppContext();
    const [state, setState] = useLocalStorage(JUST_LAUNCHED_LOCAL_STORAGE_KEY, { lastDismissedAt: 0 });
    // FIXME
    // eslint-disable-next-line react-hooks/purity
    const now = Date.now();
    const lastDismissedAt = state.lastDismissedAt ?? 0;
    const latestRelease = getCurrentReleaseNote(now);
    const lastUpdated = new Date(latestRelease?.date ?? 0).getTime();

    return {
        latestRelease,
        shouldShow: shouldShowJustLaunched(lastUpdated, now, lastDismissedAt, user),
        hasUpdate: hasUpdateJustLaunched(lastUpdated, now),
        dismiss: () => setState({ ...state, lastDismissedAt: now }),
    };
};

export const getActive = (notes: ReleaseNote[]) => (now: number) =>
    sortBy(note => note.date, notes)
        .reverse()
        .filter(note => new Date(note.date).getTime() < now);

export const getActiveReleaseNotes = getActive(releaseNotes);

export const getCurrent = (notes: ReleaseNote[]) => (now: number) => first(getActive(notes)(now));

export const getCurrentReleaseNote = getCurrent(releaseNotes);
